import { useEffect, useState } from 'react';
import type { PenPaperBulkSegment, TeacherUploadBatchStrategy } from '../../lib/classworkApi';
import { fetchPenPaperBulkImportRun } from '../../lib/classworkApi';
import { api } from '../../lib/api';

type Row = {
  student_id: string;
  title?: string;
  graded_at?: string;
  grade_value?: string;
  points_achieved?: number | null;
  points_max?: number | null;
  comment?: string | null;
};

type Props = {
  /** Für Seitenvorschau beim Sammel-PDF (Bulk-Run-Seiten als PNG). */
  classworkId?: number | null;
  /** Teilnehmerliste zur Anzeige von Klarnamen (intern bleibt die Zuordnung über student_id). */
  participantOptions?: Array<{ student_id: string; label: string }>;
  onImport: (payload: { rows: Row[]; mode: 'skip_duplicates' | 'update_existing' | 'fail_on_conflict' }) => Promise<void>;
  onUploadScan?: (payload: { student_id: string; file: File }) => Promise<void>;
  onUploadScanBulk?: (payload: { files: File[] }) => Promise<void>;
  onGeneratePrintSheets?: (payload: { layout: 'grid' | 'lines' | 'blank' }) => Promise<void>;
  onBulkImportStart?: (payload: {
    strategy: TeacherUploadBatchStrategy;
    file: File;
  }) => Promise<{ async?: boolean; run?: { id?: number; status?: string }; segments?: PenPaperBulkSegment[] } | void>;
  onBulkImportSaveSegments?: (payload: { runId: number; segments: Array<{ segment_no: number; final_student_id?: string | null; page_from: number; page_to: number }> }) => Promise<{ segments?: PenPaperBulkSegment[] } | void>;
  onBulkImportFinalize?: (payload: { runId: number }) => Promise<void>;
  loading?: boolean;
};

const EXAMPLE = `[
  {
    "student_id": "mustermann_max",
    "title": "Klassenarbeit 1",
    "graded_at": "2025-03-01 12:00:00",
    "grade_value": "2+",
    "points_achieved": 42,
    "points_max": 50,
    "comment": "optional"
  }
]`;

export default function ClassworkPenPaperImport({
  classworkId,
  participantOptions = [],
  onImport,
  onUploadScan,
  onUploadScanBulk,
  onGeneratePrintSheets,
  onBulkImportStart,
  onBulkImportSaveSegments,
  onBulkImportFinalize,
  loading
}: Props) {
  const [jsonText, setJsonText] = useState(EXAMPLE);
  const [mode, setMode] = useState<'skip_duplicates' | 'update_existing' | 'fail_on_conflict'>('skip_duplicates');
  const [showAdvancedJson, setShowAdvancedJson] = useState(false);
  const [showAlternativeWays, setShowAlternativeWays] = useState(false);
  const [showBulkAdvanced, setShowBulkAdvanced] = useState(false);
  const [scanStudentId, setScanStudentId] = useState('');
  const [bulkStrategy, setBulkStrategy] = useState<TeacherUploadBatchStrategy>('ocr_first_manual_fallback');
  const [sheetLayout, setSheetLayout] = useState<'grid' | 'lines' | 'blank'>('grid');
  const [bulkRunId, setBulkRunId] = useState<number | null>(null);
  const [bulkSegments, setBulkSegments] = useState<PenPaperBulkSegment[]>([]);
  const [bulkRunProcessing, setBulkRunProcessing] = useState(false);
  const [bulkRunStatus, setBulkRunStatus] = useState<string | null>(null);
  const [bulkRunError, setBulkRunError] = useState<string | null>(null);
  const [previewSegmentId, setPreviewSegmentId] = useState<number | null>(null);
  const [previewObjectUrlsByPage, setPreviewObjectUrlsByPage] = useState<Record<number, string>>({});

  const previewSegment =
    previewSegmentId != null ? bulkSegments.find((s) => Number(s.id) === Number(previewSegmentId)) || null : null;
  const previewUrl =
    previewSegment ? previewObjectUrlsByPage[Number(previewSegment.page_from)] || null : null;
  const previewEndpointForPage = (pageNo: number) =>
    classworkId != null && bulkRunId != null
      ? `/classwork/${classworkId}/teacher-upload/batch-import/${bulkRunId}/page/${pageNo}/preview`
      : null;
  const participantLabelById = new Map(
    participantOptions.map((p) => [String(p.student_id || '').trim(), String(p.label || '').trim()])
  );
  const labelForStudentId = (sid?: string | null) => {
    const id = String(sid || '').trim();
    if (!id) return null;
    const lbl = participantLabelById.get(id);
    return lbl ? `${lbl} (${id})` : id;
  };

  useEffect(() => {
    let cancelled = false;
    const previousUrls = Object.values(previewObjectUrlsByPage);
    // Ohne Kontext: alles freigeben
    if (classworkId == null || bulkRunId == null || !bulkSegments.length) {
      previousUrls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
      setPreviewObjectUrlsByPage({});
      return;
    }

    const run = async () => {
      const pages = Array.from(new Set(bulkSegments.map((s) => Number(s.page_from)).filter((n) => Number.isFinite(n) && n > 0)));
      const next: Record<number, string> = {};
      for (const p of pages) {
        const endpoint = previewEndpointForPage(p);
        if (!endpoint) continue;
        try {
          const res = await api.get(endpoint, { responseType: 'blob' });
          next[p] = URL.createObjectURL(res.data);
        } catch (_) {
          // Einzelne fehlerhafte Seite soll nicht die gesamte Vorschau blockieren.
        }
      }
      if (cancelled) {
        Object.values(next).forEach((u) => {
          try {
            URL.revokeObjectURL(u);
          } catch (_) {}
        });
        return;
      }
      previousUrls.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch (_) {}
      });
      setPreviewObjectUrlsByPage(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classworkId, bulkRunId, JSON.stringify(bulkSegments.map((s) => [s.id, s.page_from]))]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-3">Rückläufe einsammeln</h3>
      <p className="text-sm text-gray-600 mb-3">
        Standardweg: Sammel-PDF hochladen und danach Zuordnung prüfen.
      </p>
      {onBulkImportStart ? (
        <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/50 p-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-800">Sammel-PDF hochladen</h4>
          <p className="text-xs text-gray-600 max-w-3xl">
            Empfohlener Ablauf für ganze Klassen: PDF wählen, Zuordnung kontrollieren, finalisieren.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="btn-secondary text-sm cursor-pointer">
              Sammel-PDF wählen
              <input
                className="hidden"
                type="file"
                accept="application/pdf"
                onChange={async (e) => {
                  const input = e.target;
                  const file = input.files?.[0];
                  if (!file) return;
                  // Vor neuem Lauf alten Review-Stand leeren (sonst „hängt“ der alte Run in der UI).
                  setBulkRunId(null);
                  setBulkSegments([]);
                  setBulkRunProcessing(false);
                  setBulkRunStatus(null);
                  setBulkRunError(null);
                  try {
                    const data = await onBulkImportStart({ strategy: bulkStrategy, file });
                    const runId = Number(data?.run?.id || 0) || null;
                    const segments = Array.isArray(data?.segments) ? data.segments : [];
                    setBulkRunId(runId);
                    setBulkSegments(segments);
                    const runRow = data?.run as { status?: string; error_message?: string | null } | undefined;
                    setBulkRunStatus(runRow?.status != null ? String(runRow.status) : null);
                    setBulkRunError(runRow?.error_message != null ? String(runRow.error_message) : null);
                    const asyncRun = Boolean(data?.async);
                    const processing =
                      asyncRun ||
                      String(runRow?.status || '').toLowerCase() === 'processing';
                    setBulkRunProcessing(processing);
                    if (processing && classworkId != null && runId) {
                      const pollOnce = async () => {
                        try {
                          const polled = await fetchPenPaperBulkImportRun(classworkId, runId);
                          const run = polled?.run as { status?: string; error_message?: string | null } | undefined;
                          const segs = Array.isArray(polled?.segments) ? polled.segments : [];
                          setBulkSegments(segs);
                          setBulkRunStatus(run?.status != null ? String(run.status) : null);
                          setBulkRunError(run?.error_message != null ? String(run.error_message) : null);
                          const st = String(run?.status || '').toLowerCase();
                          if (st === 'processing') {
                            window.setTimeout(pollOnce, 2000);
                          } else {
                            setBulkRunProcessing(false);
                          }
                        } catch {
                          setBulkRunProcessing(false);
                        }
                      };
                      window.setTimeout(pollOnce, 800);
                    }
                  } finally {
                    // Immer zurücksetzen: sonst kein erneutes onChange bei gleicher Datei nach Fehler
                    input.value = '';
                  }
                }}
              />
            </label>
            {bulkRunId != null ? (
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={loading}
                onClick={() => {
                  setBulkRunId(null);
                  setBulkSegments([]);
                  setBulkRunProcessing(false);
                  setBulkRunStatus(null);
                  setBulkRunError(null);
                }}
              >
                Import zurücksetzen
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className="text-xs font-medium text-gray-700 underline"
            onClick={() => setShowBulkAdvanced((prev) => !prev)}
          >
            {showBulkAdvanced ? 'Erweiterte Importoptionen ausblenden' : 'Erweiterte Importoptionen anzeigen'}
          </button>
          {showBulkAdvanced ? (
            <div className="space-y-2 rounded-md border border-indigo-200 bg-white p-3">
              <p className="text-xs text-gray-600">
                Für Sonderfälle: Strategieauswahl und technische Hinweise zur Erkennung.
              </p>
              <select className="input-field max-w-sm text-sm" value={bulkStrategy} onChange={(e) => setBulkStrategy(e.target.value as TeacherUploadBatchStrategy)}>
                <option value="ocr_first_manual_fallback">OCR/Name-Vorschlag + manuelle Korrektur</option>
                <option value="marker_qr_required">QR/Marker-basierte Trennung</option>
              </select>
              <p className="text-xs text-gray-600">
                Optional kann der Server pro Seite ein Vision-LLM nutzen (
                <code className="bg-gray-100 px-1 rounded">classwork.batch_import.name_llm.enabled</code>
                ).
              </p>
            </div>
          ) : null}
          {bulkRunId &&
          (bulkSegments.length > 0 || bulkRunProcessing || String(bulkRunStatus || '').toLowerCase() === 'failed') ? (
            <div className="border border-gray-200 rounded-md p-3 space-y-2">
              <div className="text-xs text-gray-700">
                Run #{bulkRunId}
                {bulkRunProcessing ? (
                  <span className="ml-2 text-amber-800">· Analyse läuft (QR/OCR/KI) …</span>
                ) : null}
                {' '}
                - Segmente zuordnen (hartes Mapping vor Finalize)
              </div>
              {String(bulkRunStatus || '').toLowerCase() === 'failed' && bulkRunError ? (
                <p className="text-xs text-red-700">{bulkRunError}</p>
              ) : null}
              {bulkRunProcessing && bulkSegments.length === 0 ? (
                <p className="text-xs text-gray-600">Seiten werden im Hintergrund erkannt. Die Tabelle füllt sich automatisch.</p>
              ) : null}
              <div className="max-h-[min(70vh,36rem)] overflow-auto border border-gray-100 rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-2 py-1">Seg</th>
                      <th className="text-left px-2 py-1">Seiten</th>
                      <th className="text-left px-2 py-1">Ansicht</th>
                      <th className="text-left px-2 py-1">Vorschlag</th>
                      <th className="text-left px-2 py-1">Herkunft</th>
                      <th className="text-left px-2 py-1">Final student_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkSegments.map((s) => {
                      const previewUrl = previewObjectUrlsByPage[Number(s.page_from)] || null;
                      return (
                      <tr key={s.id} className="border-t border-gray-100">
                        <td className="px-2 py-1">{s.segment_no}</td>
                        <td className="px-2 py-1">{s.page_from}..{s.page_to}</td>
                        <td className="px-2 py-1 align-top">
                          {previewUrl ? (
                            <div className="flex flex-col gap-1 max-w-[140px]">
                              <button
                                type="button"
                                className="text-left text-primary-700 underline font-medium"
                                onClick={() => setPreviewSegmentId(Number(s.id))}
                              >
                                Im Modal öffnen
                              </button>
                              <img
                                src={previewUrl}
                                alt={`Seite ${s.page_from}`}
                                className="max-h-24 w-full object-contain rounded border border-gray-200 bg-gray-50"
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          {s.suggested_student_id ? (
                            <span title={s.suggested_student_id}>{labelForStudentId(s.suggested_student_id)}</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <span className={`inline-flex rounded px-1.5 py-0.5 ${String(s.notes || '').toLowerCase().includes('qr erkannt') ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {s.notes || '-'}
                          </span>
                        </td>
                        <td className="px-2 py-1 space-y-1">
                          <input
                            className="input-field text-xs py-1 px-2"
                            value={String(s.final_student_id || '')}
                            onChange={(e) => {
                              const v = e.target.value;
                              setBulkSegments((prev) => prev.map((x) => (x.id === s.id ? { ...x, final_student_id: v } : x)));
                            }}
                          />
                          <select
                            className="input-field text-xs py-1 px-2"
                            value={String(s.final_student_id || '')}
                            onChange={(e) => {
                              const v = e.target.value || '';
                              setBulkSegments((prev) => prev.map((x) => (x.id === s.id ? { ...x, final_student_id: v } : x)));
                            }}
                          >
                            <option value="">— Teilnehmer wählen —</option>
                            {participantOptions.map((p) => (
                              <option key={p.student_id} value={p.student_id}>
                                {p.label} ({p.student_id})
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  disabled={bulkRunProcessing}
                  onClick={async () => {
                    if (!bulkRunId || !onBulkImportSaveSegments) return;
                    const payload = bulkSegments.map((s) => ({
                      segment_no: s.segment_no,
                      final_student_id: (s.final_student_id || '').trim() || null,
                      page_from: s.page_from,
                      page_to: s.page_to
                    }));
                    const data = await onBulkImportSaveSegments({ runId: bulkRunId, segments: payload });
                    if (Array.isArray(data?.segments)) setBulkSegments(data.segments);
                  }}
                >
                  Segmente speichern
                </button>
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={bulkRunProcessing || bulkSegments.length === 0}
                  onClick={async () => {
                    if (!bulkRunId || !onBulkImportFinalize) return;
                    await onBulkImportFinalize({ runId: bulkRunId });
                  }}
                >
                  Bulk-Import finalisieren
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {(onUploadScan || onUploadScanBulk || onGeneratePrintSheets) ? (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <button
            type="button"
            className="text-xs font-medium text-gray-700 underline"
            onClick={() => setShowAlternativeWays((prev) => !prev)}
          >
            {showAlternativeWays ? 'Weitere Wege ausblenden' : 'Weitere Wege anzeigen'}
          </button>
          {showAlternativeWays ? (
            <div className="mt-3 space-y-4">
              {onUploadScan ? (
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Datei pro Schüler hochladen</h4>
                  <div className="flex flex-wrap gap-2 items-center">
                    <input
                      className="input-field max-w-xs text-sm"
                      placeholder="student_id"
                      value={scanStudentId}
                      onChange={(e) => setScanStudentId(e.target.value)}
                    />
                    <label className={`btn-secondary text-sm cursor-pointer ${!scanStudentId.trim() ? 'opacity-60 pointer-events-none' : ''}`}>
                      Scan-Datei wählen
                      <input
                        className="hidden"
                        type="file"
                        onChange={async (e) => {
                          const input = e.target;
                          const file = input.files?.[0];
                          if (!file || !scanStudentId.trim()) return;
                          try {
                            await onUploadScan({ student_id: scanStudentId.trim(), file });
                          } finally {
                            input.value = '';
                          }
                        }}
                      />
                    </label>
                  </div>
                </div>
              ) : null}
              {onUploadScanBulk ? (
                <div className="border-t border-gray-100 pt-3">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Mehrere Dateien oder ZIP automatisch zuordnen</h4>
                  <p className="text-xs text-gray-600 mb-2">
                    Mehrere Dateien direkt markieren oder eine ZIP hochladen. Die Zuordnung erfolgt automatisch über bestehende Analyse inkl. Dateinamen.
                  </p>
                  <label className="btn-secondary text-sm cursor-pointer">
                    Dateien/ZIP wählen
                    <input
                      className="hidden"
                      type="file"
                      multiple
                      accept=".zip,application/zip,application/pdf,image/*,.doc,.docx,.odt,.ods,.odp,.txt,.csv,.ppt,.pptx,.xls,.xlsx"
                      onChange={async (e) => {
                        const input = e.target;
                        const files = Array.from(input.files || []);
                        if (!files.length) return;
                        try {
                          await onUploadScanBulk({ files });
                        } finally {
                          input.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
              ) : null}
              {onGeneratePrintSheets ? (
                <div className="border-t border-gray-100 pt-3 space-y-2">
                  <h4 className="text-sm font-semibold text-gray-800">Klassenarbeitspapier herunterladen</h4>
                  <div className="flex flex-wrap items-center gap-2">
                    <select className="input-field max-w-xs text-sm" value={sheetLayout} onChange={(e) => setSheetLayout(e.target.value as 'grid' | 'lines' | 'blank')}>
                      <option value="grid">Rechenkaros</option>
                      <option value="lines">Schreiblinien</option>
                      <option value="blank">Blanko</option>
                    </select>
                    <button
                      type="button"
                      className="btn-primary text-sm"
                      disabled={loading}
                      onClick={async () => {
                        await onGeneratePrintSheets({ layout: sheetLayout });
                      }}
                    >
                      PDF erzeugen & herunterladen
                    </button>
                  </div>
                  <p className="text-xs text-gray-600">Enthält Name, Vorname, Klasse und QR-Code pro Schülerseite.</p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="mt-4 border-t border-gray-100 pt-3">
        <button
          type="button"
          className="text-xs font-medium text-gray-700 underline"
          onClick={() => setShowAdvancedJson((prev) => !prev)}
        >
          {showAdvancedJson ? 'Erweiterte Optionen ausblenden' : 'Erweiterte Optionen (JSON-Import) anzeigen'}
        </button>
        {showAdvancedJson ? (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-gray-600">
              Nur für Sonderfälle: Direkter JSON-Import wie von der API
              {' '}
              <code className="bg-gray-100 px-1 rounded">POST /classwork/:id/teacher-upload/import</code>
              {' '}
              erwartet.
            </p>
            <label className="block text-xs font-medium text-gray-700">Modus</label>
            <select className="input-field max-w-md" value={mode} onChange={(e) => setMode(e.target.value as any)}>
              <option value="skip_duplicates">Duplikate überspringen</option>
              <option value="update_existing">Bestehende aktualisieren</option>
              <option value="fail_on_conflict">Bei Konflikt abbrechen</option>
            </select>
            <textarea
              className="input-field w-full min-h-[180px] font-mono text-xs"
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
            />
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={loading}
              onClick={async () => {
                let rows: Row[] = [];
                try {
                  const parsed = JSON.parse(jsonText);
                  if (!Array.isArray(parsed)) throw new Error('JSON muss ein Array sein');
                  rows = parsed;
                } catch {
                  window.alert('Ungültiges JSON');
                  return;
                }
                await onImport({ rows, mode });
              }}
            >
              JSON-Import starten
            </button>
          </div>
        ) : null}
      </div>
      {previewSegment ? (
        <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  Seite {previewSegment.page_from} (Segment {previewSegment.segment_no})
                </div>
                <div className="text-xs text-gray-600">Direkte manuelle Zuordnung im Modal</div>
              </div>
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => setPreviewSegmentId(null)}
              >
                Schließen
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              <div className="lg:col-span-2 bg-gray-100 p-3 min-h-[40vh] max-h-[78vh] overflow-auto">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={`Seite ${previewSegment.page_from}`}
                    className="w-full h-auto object-contain rounded border border-gray-300 bg-white"
                  />
                ) : (
                  <div className="text-sm text-gray-500">Keine Vorschau verfügbar.</div>
                )}
              </div>
              <div className="p-4 border-t lg:border-t-0 lg:border-l border-gray-200 space-y-3">
                <div className="text-xs text-gray-600">
                  Vorschlag: <span className="font-medium text-gray-900">{labelForStudentId(previewSegment.suggested_student_id) || '-'}</span>
                </div>
                <div className="text-xs text-gray-600">
                  Info: <span className="font-medium text-gray-900">{previewSegment.notes || '-'}</span>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Final student_id</label>
                  <input
                    className="input-field text-sm"
                    value={String(previewSegment.final_student_id || '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBulkSegments((prev) =>
                        prev.map((x) => (x.id === previewSegment.id ? { ...x, final_student_id: v } : x))
                      );
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Teilnehmer (Klarname)</label>
                  <select
                    className="input-field text-sm"
                    value={String(previewSegment.final_student_id || '')}
                    onChange={(e) => {
                      const v = e.target.value || '';
                      setBulkSegments((prev) =>
                        prev.map((x) => (x.id === previewSegment.id ? { ...x, final_student_id: v } : x))
                      );
                    }}
                  >
                    <option value="">— Teilnehmer wählen —</option>
                    {participantOptions.map((p) => (
                      <option key={p.student_id} value={p.student_id}>
                        {p.label} ({p.student_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={loading}
                    onClick={async () => {
                      if (!bulkRunId || !onBulkImportSaveSegments) return;
                      const payload = bulkSegments.map((s) => ({
                        segment_no: s.segment_no,
                        final_student_id: (s.final_student_id || '').trim() || null,
                        page_from: s.page_from,
                        page_to: s.page_to
                      }));
                      const data = await onBulkImportSaveSegments({ runId: bulkRunId, segments: payload });
                      if (Array.isArray(data?.segments)) setBulkSegments(data.segments);
                    }}
                  >
                    Zuordnung speichern
                  </button>
                  <button
                    type="button"
                    className="btn-primary text-sm"
                    disabled={loading}
                    onClick={() => setPreviewSegmentId(null)}
                  >
                    Fertig
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
